import type { ApiConfig, IdParams } from '@api/types';
import { typedMethodPost, typedMethodGet } from '@api/utils';
import type {
  AvailableMachinesRequestDto,
  AvailableMachinesResponseDto,
  ValidateMachineEndpointRequestDto,
  ConfigureMachineRequestDto,
  ConfigureOperationRequestDto,
  ConfigureOperationResponseDto,
  StartMachineOnboardingSessionRequestDto,
  StartDemoMachineOnboardingSessionRequestDto,
  SendNcFileToMachineRequestDto,
  ValidateMachineByAutomaticControlRequestDto,
  ValidateMachineByConnectionMethodRequestDto,
  ValidateMachineByBrandRequestDto,
  OnboardMachineRequestDto,
  OnboardDemoMachineRequestDto,
  MachineDto,
  MachineGroupDto,
  OperationDto,
  DemoMachineTypeDto,
  ConfigureMachineGroupRequestDto,
  ConfigureMachineGroupResponseDto,
  RemoveMachineRequestDto,
  SwitchMachineConnectionRequestDto,
  MachineEventDto,
  MachineStatsHourDto,
  NcFileStatsHourDto,
  MachineStatsHourAllDto,
  MachineStatsMonthDto,
  NcFileStatsMonthDto,
  MachineStatsMonthAllDto,
  MachineStatsDayAllDto,
  MachineStatsDayDto,
  MachineStatsWeekDto,
  MachineStatsWeekAllDto,
  NcFileStatsDayDto,
  MachineBrandDto,
  MachineConnectionMethodDto,
  MachineCountingProfileDto,
  MachineAlarmEventDto,
  ConfigureProductionOperationRequestDto,
  MachineReferencesRequestDto,
  MachineReferencesResponseDto,
  ConfigureMachineReferencesRequestDto,
  ConfigureMachineReferencesResponseDto,
  RetrieveRawMachineEventDataRequestDto,
  RetrieveRawMachineEventDataResponseDto,
} from '../../generated/models';
import type {
  MachineIdParams,
  MachineHistoryParams,
  OperationsParams,
  MachineEventParams,
  BatchTimelineParams,
  StatsParams,
  StatsAllParams,
  MachineAlarmParams,
} from './types';

import type {
  Machine,
  MachineGroup,
  MachineEvent,
  MachineBrand,
  MachineConnectionMethod,
  MachineCountingProfile,
  MachineAlarmEvent,
  Operation,
  DemoMachineType,
  MachineStatsHour,
  MachineStatsDay,
  MachineStatsMonth,
  MachineStatsWeek,
  MachineStatsHourAll,
  MachineStatsDayAll,
  MachineStatsMonthAll,
  MachineStatsWeekAll,
  NcFileStatsHour,
  NcFileStatsDay,
  NcFileStatsMonth,
  AvailableMachinesResponse,
  ConfigureOperationResponse,
  ConfigureMachineGroupResponse,
  MachineReferencesResponse,
  ConfigureMachineReferencesResponse,
  RetrieveRawMachineEventDataResponse,
} from '../../domain/types';

import {
  toMachines,
  toMachineGroups,
  toMachineEvents,
  toMachineBrands,
  toMachineConnectionMethods,
  toMachineAlarmEvents,
  toOperations,
  toDemoMachineTypes,
  toMachineStatsHours,
  toMachineStatsDays,
  toMachineStatsMonths,
  toMachineStatsWeeks,
  toMachineStatsHourAlls,
  toMachineStatsDayAlls,
  toMachineStatsMonthAlls,
  toMachineStatsWeekAlls,
  toNcFileStatsHours,
  toNcFileStatsDays,
  toNcFileStatsMonths,
  toAvailableMachinesResponse,
  toConfigureOperationResponse,
  toConfigureMachineGroupResponse,
  toMachineReferencesResponse,
  toConfigureMachineReferencesResponse,
  toRetrieveRawMachineEventDataResponse,
  toMachineCountingProfiles,
} from '../../domain/mappers';

export default ({ baseURL, baseURLoData }: ApiConfig) => ({
  availableMachines: typedMethodPost<AvailableMachinesRequestDto, AvailableMachinesResponseDto, AvailableMachinesResponse>(
    (data) =>
      fetch(`${baseURL}/v1/machines/availableMachines`, {
        method: 'POST',
        ...data,
      }),
    toAvailableMachinesResponse
  ),
  validateMachineOnboardingEndpoint: typedMethodPost<ValidateMachineEndpointRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/machines/validateMachineOnboardingEndpoint`, {
        method: 'POST',
        ...data,
      })
  ),
  configure: typedMethodPost<ConfigureMachineRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/machines/configure`, {
        method: 'POST',
        ...data,
      })
  ),
  configureOperations: typedMethodPost<ConfigureOperationRequestDto, ConfigureOperationResponseDto, ConfigureOperationResponse>(
    (data) =>
      fetch(`${baseURL}/v1/machines/configureOperation`, {
        method: 'POST',
        ...data,
      }),
    toConfigureOperationResponse
  ),
  startMachineOnboardingSession: typedMethodPost<StartMachineOnboardingSessionRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/machines/startMachineOnboardingSession`, {
        method: 'POST',
        ...data,
      })
  ),
  startDemoMachineOnboardingSession: typedMethodPost<StartDemoMachineOnboardingSessionRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/machines/startDemoMachineOnboardingSession`, {
        method: 'POST',
        ...data,
      })
  ),
  sendNcFileToMachine: typedMethodPost<SendNcFileToMachineRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v2/machines/sendNcFileToMachine`, {
        method: 'POST',
        ...data,
      })
  ),
  validateMachineOnboardingByAutomaticControl: typedMethodPost<ValidateMachineByAutomaticControlRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/machines/validateMachineOnboardingByAutomaticControl`, {
        method: 'POST',
        ...data,
      })
  ),
  validateMachineOnboardingByConnectionMethod: typedMethodPost<ValidateMachineByConnectionMethodRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/machines/validateMachineOnboardingByConnectionMethod`, {
        method: 'POST',
        ...data,
      })
  ),
  validateMachineOnboardingByBrand: typedMethodPost<ValidateMachineByBrandRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/machines/validateMachineOnboardingByBrand`, {
        method: 'POST',
        ...data,
      })
  ),
  onboardMachine: typedMethodPost<OnboardMachineRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/machines/onboardMachine`, {
        method: 'POST',
        ...data,
      })
  ),
  onboardDemoMachine: typedMethodPost<OnboardDemoMachineRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/machines/onboardDemoMachine`, {
        method: 'POST',
        ...data,
      })
  ),
  search: typedMethodGet<MachineIdParams, MachineDto[], Machine[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/Machine?$orderby=FullName asc&$filter=IsActive eq true ${
          data?.params?.id ? `and machineId eq ${data.params.id}` : ''
        }`,
        {
          method: 'GET',
          headers: data.headers,
        }
      ),
    toMachines
  ),
  getMachineById: typedMethodGet<IdParams, MachineDto[], Machine[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/Machine?$filter=machineId eq ${data.params.id}`, {
        method: 'GET',
        headers: data.headers,
      }),
    toMachines
  ),
  getMachineFromHistory: typedMethodGet<MachineHistoryParams, MachineDto[], Machine[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/Machine?$filter=(machineConnection/macAddress eq '${data.params.macAddress}' OR machineConnection/endpoint eq '${data.params.endpoint}' OR fullName eq '${data.params.fullName}')&$orderby=createdOn desc`,
        {
          method: 'GET',
          headers: data.headers,
        }
      ),
    toMachines
  ),
  getMachinesGroup: typedMethodGet<void, MachineGroupDto[], MachineGroup[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/MachineGroup?$expand=machines($filter=isActive eq true)`, {
        method: 'GET',
        headers: data.headers,
      }),
    toMachineGroups
  ),
  getMachinesOperations: typedMethodGet<OperationsParams, OperationDto[], Operation[]>(
    (data) => {
      const order = data?.params?.orderBy;
      const filter = data?.params?.filter;
      const orderExist = order != null;

      const filterPart = !!filter ? `$filter=${filter}` : '';
      const expandPart = `$expand=machines($filter=isActive eq true)`;
      const orderPart = orderExist ? `$orderby=${order}` + ' ' : '';

      const queryParts = [filterPart, orderPart, expandPart].filter(Boolean).join('&');

      const url = `${baseURLoData}/odata/Operation?${queryParts}`;

      return fetch(url, {
        method: 'GET',
        headers: data.headers,
      });
    },
    toOperations
  ),
  demoMachineType: typedMethodGet<void, DemoMachineTypeDto[], DemoMachineType[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/DemoMachineType`, {
        method: 'GET',
        headers: data.headers,
      }),
    toDemoMachineTypes
  ),
  configureGroup: typedMethodPost<ConfigureMachineGroupRequestDto, ConfigureMachineGroupResponseDto, ConfigureMachineGroupResponse>(
    (data) =>
      fetch(`${baseURL}/v1/machines/configureGroup`, {
        method: 'POST',
        ...data,
      }),
    toConfigureMachineGroupResponse
  ),
  deleteMachine: typedMethodPost<RemoveMachineRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/machines/remove`, {
        method: 'POST',
        ...data,
      })
  ),
  switchConnection: typedMethodPost<SwitchMachineConnectionRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/machines/switchConnection`, {
        method: 'POST',
        ...data,
      })
  ),
  machineEvent: typedMethodGet<MachineEventParams, MachineEventDto[], MachineEvent[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/MachineEvent?$apply=filter(MachineId eq ${data.params.id} and NcSmartLinkIdMain eq null and SubStatus ne 'Offline' and StartedOn le ${data.params.end} and EndedOn ge ${data.params.start})/groupby((NcFileMain))`,
        {
          method: 'GET',
          headers: data.headers,
        }
      ),
    toMachineEvents
  ),
  machineEventForBatchTimeline: typedMethodGet<BatchTimelineParams, MachineEventDto[], MachineEvent[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/MachineEvent?$filter=ProductionBatchId eq ${data.params.productionBatchId} and StartedOn ge ${data.params.start} and EndedOn lt ${data.params.end}&$orderBy=startedOn asc`,
        {
          method: 'GET',
          headers: data.headers,
        }
      ),
    toMachineEvents
  ),
  machineStatsHour: typedMethodGet<StatsParams, MachineStatsHourDto[], MachineStatsHour[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/MachineStatsHour?$filter=machineId eq ${data.params.id} and dateTime ge ${data.params.start} and dateTime lt ${data.params.end}&$orderby=dateTime asc`,
        {
          method: 'GET',
          headers: data.headers,
        }
      ),
    toMachineStatsHours
  ),
  ncFileStatsHour: typedMethodGet<StatsParams, NcFileStatsHourDto[], NcFileStatsHour[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/NcFileStatsHour?$filter=ncFileId eq ${data.params.id} and dateTime ge ${data.params.start} and dateTime lt ${data.params.end}&$orderby=dateTime asc`,
        {
          method: 'GET',
          headers: data.headers,
        }
      ),
    toNcFileStatsHours
  ),
  machineStatsHourAll: typedMethodGet<StatsAllParams, MachineStatsHourAllDto[], MachineStatsHourAll[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/machineStatsHourAll?$filter=dateTime ge ${data.params.start} and dateTime lt ${data.params.end}&$orderby=dateTime asc`,
        {
          method: 'GET',
          headers: data.headers,
        }
      ),
    toMachineStatsHourAlls
  ),
  machineStatsMonth: typedMethodGet<StatsParams, MachineStatsMonthDto[], MachineStatsMonth[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/MachineStatsMonth?$filter=machineId eq ${data.params.id} and Date ge ${data.params.start} and Date lt ${data.params.end}&$orderby=Date asc`,
        {
          method: 'GET',
          headers: data.headers,
        }
      ),
    toMachineStatsMonths
  ),
  ncFileStatsMonth: typedMethodGet<StatsParams, NcFileStatsMonthDto[], NcFileStatsMonth[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/NcFileStatsMonth?$filter=ncFileId eq ${data.params.id} and Date ge ${data.params.start} and Date lt ${data.params.end}&$orderby=Date asc`,
        {
          method: 'GET',
          headers: data.headers,
        }
      ),
    toNcFileStatsMonths
  ),
  machineStatsMonthAll: typedMethodGet<StatsAllParams, MachineStatsMonthAllDto[], MachineStatsMonthAll[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/MachineStatsMonthAll?$filter=Date ge ${data.params.start} and Date lt ${data.params.end}&$orderby=Date asc`,
        {
          method: 'GET',
          headers: data.headers,
        }
      ),
    toMachineStatsMonthAlls
  ),
  machineStatsDayAll: typedMethodGet<StatsAllParams, MachineStatsDayAllDto[], MachineStatsDayAll[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/machineStatsDayAll?$filter=date ge ${data.params.start} and date lt ${data.params.end}&$orderby=date asc`,
        {
          method: 'GET',
          headers: data.headers,
        }
      ),
    toMachineStatsDayAlls
  ),
  machineStatsDay: typedMethodGet<StatsParams, MachineStatsDayDto[], MachineStatsDay[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/MachineStatsDay?$filter=machineId eq ${data.params.id} and date ge ${data.params.start} and date lt ${data.params.end}&$orderby=date asc`,
        {
          method: 'GET',
          headers: data.headers,
        }
      ),
    toMachineStatsDays
  ),
  machineStatsWeek: typedMethodGet<StatsParams, MachineStatsWeekDto[], MachineStatsWeek[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/MachineStatsWeek?$filter=machineId eq ${data.params.id} and date ge ${data.params.start} and date lt ${data.params.end}&$orderby=date asc`,
        {
          method: 'GET',
          headers: data.headers,
        }
      ),
    toMachineStatsWeeks
  ),
  machineStatsWeekAll: typedMethodGet<StatsAllParams, MachineStatsWeekAllDto[], MachineStatsWeekAll[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/MachineStatsWeekAll?$filter=date ge ${data.params.start} and date lt ${data.params.end}&$orderby=date asc`,
        {
          method: 'GET',
          headers: data.headers,
        }
      ),
    toMachineStatsWeekAlls
  ),
  ncFileStatsDay: typedMethodGet<StatsParams, NcFileStatsDayDto[], NcFileStatsDay[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/NcFileStatsDay?$filter=ncFileId eq ${data.params.id} and date ge ${data.params.start} and date lt ${data.params.end}&$orderby=date asc`,
        {
          method: 'GET',
          headers: data.headers,
        }
      ),
    toNcFileStatsDays
  ),
  machineBrand: typedMethodGet<void, MachineBrandDto[], MachineBrand[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/MachineBrand`, {
        method: 'GET',
        headers: data.headers,
      }),
    toMachineBrands
  ),
  machineConnectionMethod: typedMethodGet<void, MachineConnectionMethodDto[], MachineConnectionMethod[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/MachineConnectionMethod`, {
        method: 'GET',
        headers: data.headers,
      }),
    toMachineConnectionMethods
  ),
  machineCountingProfiles: typedMethodGet<void, MachineCountingProfileDto[], MachineCountingProfile[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/v2/MachineCountingProfiles`, {
        method: 'GET',
        headers: data.headers,
      }),
    toMachineCountingProfiles
  ),
  machineAlarmEvent: typedMethodGet<MachineAlarmParams, MachineAlarmEventDto[], MachineAlarmEvent[]>(
    (data) => {
      const idFilter = data?.params?.id
        ? `MachineId eq ${data.params.id}`
        : data?.params?.productionBatchId
          ? `ProductionBatchId eq ${data.params.productionBatchId}`
          : '';

      const shouldAddStartDateParam = data.params?.start;
      const shouldAddEndDateParam = data.params?.end;

      const url =
        `${baseURLoData}/odata/MachineAlarmEvent?$filter=${idFilter}` +
        `${shouldAddStartDateParam ? ` and EndedOn gt ${data.params.start}` : ''}` +
        `${shouldAddEndDateParam ? ` and StartedOn lt ${data.params.end}` : ''}`;

      return fetch(url, {
        method: 'GET',
        headers: data.headers,
      });
    },
    toMachineAlarmEvents
  ),
  configureProductionOperation: typedMethodPost<ConfigureProductionOperationRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/productionInformation/configureProductionOperation`, {
        method: 'POST',
        ...data,
      })
  ),
  getMachinesReferencesBySourcesSystem: typedMethodPost<MachineReferencesRequestDto, MachineReferencesResponseDto, MachineReferencesResponse>(
    (data) =>
      fetch(`${baseURL}/v2/machines/machineReferences`, {
        method: 'POST',
        ...data,
      }),
    toMachineReferencesResponse
  ),
  configureMachineReferences: typedMethodPost<ConfigureMachineReferencesRequestDto, ConfigureMachineReferencesResponseDto, ConfigureMachineReferencesResponse>(
    (data) =>
      fetch(`${baseURL}/v2/machines/configureMachineReferences`, {
        method: 'POST',
        ...data,
      }),
    toConfigureMachineReferencesResponse
  ),
  retrieveRawMachineEventData: typedMethodPost<RetrieveRawMachineEventDataRequestDto, RetrieveRawMachineEventDataResponseDto, RetrieveRawMachineEventDataResponse>(
    (data) =>
      fetch(`${baseURL}/v2/machines/retrieveRawMachineEventData`, {
        method: 'POST',
        ...data,
      }),
    toRetrieveRawMachineEventDataResponse
  ),
});

// Re-export domain types for consumers
export type {
  Machine,
  MachineGroup,
  MachineEvent,
  MachineBrand,
  MachineConnectionMethod,
  MachineAlarmEvent,
  Operation,
  DemoMachineType,
  MachineStatsHour,
  MachineStatsDay,
  MachineStatsMonth,
  MachineStatsWeek,
  MachineStatsHourAll,
  MachineStatsDayAll,
  MachineStatsMonthAll,
  MachineStatsWeekAll,
  NcFileStatsHour,
  NcFileStatsDay,
  NcFileStatsMonth,
  AvailableMachinesResponse,
  ConfigureOperationResponse,
  ConfigureMachineGroupResponse,
  MachineReferencesResponse,
  ConfigureMachineReferencesResponse,
  RetrieveRawMachineEventDataResponse,
  MachineCountingProfile,
} from '../../domain/types';

// Re-export request DTOs (still needed for building requests)
export type {
  AvailableMachinesRequestDto,
  ValidateMachineEndpointRequestDto,
  ConfigureMachineRequestDto,
  ConfigureOperationRequestDto,
  StartMachineOnboardingSessionRequestDto,
  StartDemoMachineOnboardingSessionRequestDto,
  SendNcFileToMachineRequestDto,
  ValidateMachineByAutomaticControlRequestDto,
  ValidateMachineByConnectionMethodRequestDto,
  ValidateMachineByBrandRequestDto,
  OnboardMachineRequestDto,
  OnboardDemoMachineRequestDto,
  ConfigureMachineGroupRequestDto,
  RemoveMachineRequestDto,
  SwitchMachineConnectionRequestDto,
  ConfigureProductionOperationRequestDto,
  MachineReferencesRequestDto,
  ConfigureMachineReferencesRequestDto,
  RetrieveRawMachineEventDataRequestDto,
} from '../../generated/models';

export type {
  MachineIdParams,
  MachineHistoryParams,
  OperationsParams,
  MachineEventParams,
  BatchTimelineParams,
  StatsParams,
  StatsAllParams,
  MachineAlarmParams,
} from './types';
